import { useState } from "react";
import { useTranslation } from "react-i18next";

import { localizeAmountWithCurrency } from "libs/design-system/src/utils/localizeAmountWithCurrency";
import { Checkbox, CheckboxBase, formatAmountWithCommas, Input } from "@unified-checkout/design-system";

import type { IRefundModal } from "@app/dashboard/types/innerLayout/transactions/modals/refund";
import { convertCentsToMajorUnit } from "@app/dashboard/utils/convertAmounts";
import { AANI, calculateInstantRefundFee } from "../../utils";

import styles from "./refundModalBody.module.scss";

const defaultText: IRefundModal['localization'] = {
  quantity: 'Quantity',
  totalAmount: 'Total amount',
  refundableAmount: 'Refundable amount',
  refundFullAmount: 'Refund full amount'
};

export const RefundModalBody = ({
  lang,
  totalAmount,
  refundableAmount,
  currency,
  products,
  toBeRefundedProducts,
  emptyStateText,
  refundType,
  canPartiallyRefunded,
  isDefaultPartialRefund,
  partialRefundInput,
  onRefundInputToggle,
  additionalInput,
  localization = defaultText,
  loading,
  cafDetails,
  isInstantRefund, 
  paymentMethod = ''
}: IRefundModal) => {
  const { t } = useTranslation();

  const [checkedProducts, setCheckedProducts] = useState<number[]>((products || [])?.map(item => item.id));
  const [isPartialRefund, setIsPartialRefund] = useState(isDefaultPartialRefund);

  const handleChange = (checked: boolean, id: number) => {
    setCheckedProducts((prevChecked) => {
      let ids;

      if (checked) {
        ids = prevChecked.includes(id) ? prevChecked : [...prevChecked, id];
      } else {
        ids = prevChecked.filter((itemId) => itemId !== id);
      };

      const price = products?.
        filter(product => ids.includes(product.id))
        .reduce((sum, product) => sum + product.price, 0) || 0;

      toBeRefundedProducts?.(ids, price);

      return ids;
    });
  };

  const calculatePartialAmount = (formatWithCommas?: boolean) => {
    const amount = checkedProducts.reduce((sum, id) => {
      const item = products?.find(item => item.id === id);
      return sum + (item ? item.price : 0);
    }, 0);

    return formatWithCommas ? formatAmountWithCommas({ amount }) : amount;
  };

  const calculateConvenienceFees = () => {
    if(!cafDetails) return;

    const convenienceFeeCents = calculateInstantRefundFee(cafDetails);    

    const fees = localizeAmountWithCurrency({
      amount: convertCentsToMajorUnit(convenienceFeeCents, currency),
      currency,
      lang: lang as string,
      asString: true,
    });

    return fees
  }  

  const getCafHintText = () => {
    if (cafDetails && cafDetails.refund_caf_amount === false && cafDetails.convenience_fee_amount) {
      const cafAmount = convertCentsToMajorUnit(cafDetails.convenience_fee_amount, currency);
      const cafCurrency = cafDetails?.convenience_fee_currency || currency;
      const formattedCafAmount = `${cafCurrency} ${formatAmountWithCommas({ amount: cafAmount, currency: cafCurrency })}`;
      return t('innerLayout.transactionsPage.singleTransactionsPage.modals.refund.cafNonRefundableHint', { amount: formattedCafAmount });
    }
    return undefined;
  };

  const refundNotes = [
    {
      condition: isInstantRefund,
      content: t(
        'innerLayout.transactionsPage.singleTransactionsPage.modals.refund.instantRefundNote',
        { fees: calculateConvenienceFees() }
      ),
    },
    {
      condition: !!getCafHintText(),
      content: getCafHintText(),
    },
    {
      condition: [AANI].includes(paymentMethod),
      content: t('innerLayout.transactionsPage.singleTransactionsPage.modals.refund.refundOneTimeNote'),
    },
  ];

  const activeNote = refundNotes.find((note) => note.condition);

  return (
    <div className={styles["refund-modal-body"]}>
      {
        refundType === 'products' &&
        <>
          {
            emptyStateText &&
            <div className={styles["refund-modal-body__empty-state"]}>
              {emptyStateText}
            </div>
          }
          {
            products?.map((item, index) =>
              <div className={styles["refund-modal-body__item"]} key={`list-box-item-${index}`}>

                <CheckboxBase
                  id={String(item.id)}
                  onChangeHandler={(checked) => handleChange(checked, item.id)}
                  checked={true}
                  disabled={loading}
                />

                {
                  item.img &&
                  <div className={styles["refund-modal-body__item__img"]}>
                    {
                      typeof item.img === 'string' ?
                        <img src={item.img} alt={item.name} />
                        :
                        item.img
                    }
                  </div>
                }

                <div className={styles["refund-modal-body__item__info"]}>
                  {
                  item.name &&
                    <div className={styles["refund-modal-body__item__info__name"]}>
                      {item.name}
                    </div>
                  }
                  {
                  item.category &&
                    <div className={styles["refund-modal-body__item__info__category"]}>
                      {item.category}
                    </div>
                  }
                </div>

              <div className={styles["refund-modal-body__item__price-quantity-box"]}>
                  <div className={styles["refund-modal-body__item__price-quantity-box__price"]}>
                    {lang === 'en' && item.currency}{" "}
                      {formatAmountWithCommas({amount: item.price})}
                    {" "}{lang === 'ar' && item.currency}
                  </div>

                  {
                  item.quantity &&
                    <div className={styles["refund-modal-body__item__price-quantity-box__quantity"]}>
                      {`${localization?.quantity}: ${item.quantity}`}
                    </div>
                  }
                </div>
              </div>
            )
          }
        </>
      }

      <div className={styles["refund-modal-body__amount-box"]}>
        <span className={styles["refund-modal-body__amount-box__label"]}>
          {localization?.totalAmount}
        </span>
        <span className={styles["refund-modal-body__amount-box__amount"]}>
          {lang === 'en' && currency}{" "}
          {refundType === "products" ? <>{calculatePartialAmount(true)}</> : totalAmount}
          {" "}{lang === 'ar' && currency}
        </span>
      </div>

      {
        refundableAmount &&
        <div className={styles["refund-modal-body__amount-box"]}>
          <span className={styles["refund-modal-body__amount-box__label"]}>
            {localization?.refundableAmount}
          </span>
          <span className={styles["refund-modal-body__amount-box__amount"]}>
            {lang === 'en' && currency}{" "}
            {refundableAmount}
            {" "}{lang === 'ar' && currency}
          </span>
        </div>
      }

      {
        refundType === "transaction" &&
        <>
          {
            canPartiallyRefunded &&
            <Checkbox
              id="modal-partial-refund-checkbox"
              text={localization?.refundFullAmount}
              onChangeHandler={(checked) => { setIsPartialRefund(!checked); onRefundInputToggle?.(!checked) }}
              checked={!isDefaultPartialRefund}
              disabled={loading}
            />
          }

          {
            canPartiallyRefunded && isPartialRefund &&
            <div className={styles["refund-modal-body__partial-refund-input"]}>
              <Input
                name={partialRefundInput?.name || "modal-partial-refund-field"}
                label={partialRefundInput?.label}
                type="number"
                value={partialRefundInput?.value}
                error={partialRefundInput?.error}
                disabled={loading}
                status={partialRefundInput?.error ? 'error' : 'default'}
                placeholder={partialRefundInput?.placeholder}
                onChange={partialRefundInput?.onChangeHandler}
                leadingText={partialRefundInput?.leadingText}
              />
            </div>
          }

          {
            additionalInput &&
            <Input
              name={additionalInput?.name || "additional-input-field"}
              label={additionalInput?.label}
              value={additionalInput?.value}
              error={additionalInput?.error}
              disabled={loading}
              status={additionalInput?.error ? 'error' : 'default'}
              placeholder={additionalInput?.placeholder}
              onChange={additionalInput?.onChangeHandler}
              leadingText={additionalInput?.leadingText}
            />
          }
        </>
      }
      {activeNote && (
        <p className={styles['refund-modal-body__instant-refund-note']}>
          {activeNote.content}
        </p>
      )}
    </div>
  )
};